import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';
import { api } from '../services/api';
import ParishRatesPanel from '../components/ParishRatesPanel';

interface Row {
  id: string;
  state: string;
  state_code: string;
  threshold_amount: string;
  has_sales_tax: boolean;
  wholesale_counts: boolean;
  rate: string | null;
  permit_number: string | null;
  permit_registered_at: string | null;
  filing_frequency: string | null;
  last_filed_at: string | null;
  collection_enabled: boolean;
}

const SST_STATES = new Set([
  'AR','GA','IN','IA','KS','KY','MI','MN','NE','NV','NJ','NC','ND','OH','OK','RI','SD','TN','UT','VT','WA','WV','WI','WY',
]);

export default function SalesTaxNexusPage() {
  const [rows, setRows] = useState<Row[]>([]);
  const [available, setAvailable] = useState<Array<{ state: string; state_code: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Row | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerState, setPickerState] = useState('');
  const [form, setForm] = useState({
    rate: '',
    permit_number: '',
    permit_registered_at: '',
    filing_frequency: 'quarterly',
    last_filed_at: '',
    collection_enabled: false,
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [data, avail] = await Promise.all([
        api.tax.listCollection(),
        api.tax.availableStates(),
      ]);
      setRows(data as Row[]);
      setAvailable(avail as any);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (r: Row | { state_code: string; state: string }) => {
    const cur = (r as Row);
    setEditing(cur);
    setForm({
      rate: cur.rate || '',
      permit_number: cur.permit_number || '',
      permit_registered_at: cur.permit_registered_at?.slice(0, 10) || '',
      filing_frequency: cur.filing_frequency || 'quarterly',
      last_filed_at: cur.last_filed_at?.slice(0, 10) || '',
      collection_enabled: cur.collection_enabled || false,
    });
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    setError(null);
    try {
      await api.tax.setCollection(editing.state_code, {
        rate: form.rate ? parseFloat(form.rate) : null,
        permit_number: form.permit_number || null,
        permit_registered_at: form.permit_registered_at || null,
        filing_frequency: form.filing_frequency || null,
        last_filed_at: form.last_filed_at || null,
        collection_enabled: form.collection_enabled,
      });
      setEditing(null);
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (r: Row) => {
    if (!r.rate) {
      alert('Set a rate first before enabling collection.');
      startEdit(r);
      return;
    }
    const verb = r.collection_enabled ? 'STOP' : 'START';
    if (!confirm(`${verb} collecting tax for ${r.state}? Affects all NEW orders shipping to ${r.state_code}.`)) return;
    try {
      await api.tax.setCollection(r.state_code, { collection_enabled: !r.collection_enabled });
      await load();
    } catch (e: any) {
      alert(e.message);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tax Collection Configuration"
        description="Per-state rate, permit #, and filing schedule for tax collection. For nexus risk + thresholds, see Reports → Economic Nexus Tracker."
      />

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm">{error}</div>}

      <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-900">
        <strong>Where do thresholds come from?</strong> The economic-nexus thresholds + wholesale rules live on the
        <Link to="/reports/nexus" className="underline mx-1">Economic Nexus Tracker</Link>
        (Reports section). Use this page only to enter the rate / permit # / filing schedule for states where you've registered to collect.
      </div>

      <Card title="Configured states">
        <div className="flex justify-between items-center mb-3">
          <p className="text-sm text-gray-600">
            {rows.length} state{rows.length === 1 ? '' : 's'} configured · {rows.filter(r => r.collection_enabled).length} actively collecting
          </p>
          <button
            onClick={() => { setPickerState(available[0]?.state_code || ''); setShowPicker(true); }}
            className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
          >
            + Add state
          </button>
        </div>
        {loading ? <LoadingSpinner /> : rows.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No states configured for collection yet. Click "+ Add state" above to start.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 border-b">
                  <th className="px-2 py-2">State</th>
                  <th className="px-2 py-2 text-right">Rate</th>
                  <th className="px-2 py-2">Permit #</th>
                  <th className="px-2 py-2">Filing</th>
                  <th className="px-2 py-2">Last filed</th>
                  <th className="px-2 py-2 text-center">Collecting</th>
                  <th className="px-2 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.state_code} className="border-b border-gray-100">
                    <td className="px-2 py-2 font-mono font-semibold">
                      {r.state_code}
                      {SST_STATES.has(r.state_code) && (
                        <span className="ml-1 text-[10px] bg-purple-100 text-purple-700 px-1 rounded">SST</span>
                      )}
                      <div className="text-xs text-gray-500 font-normal">{r.state}</div>
                    </td>
                    <td className="px-2 py-2 text-right font-mono">
                      {r.rate ? `${(parseFloat(r.rate) * 100).toFixed(3)}%` : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-2 py-2 font-mono text-xs">{r.permit_number || <span className="text-gray-400">—</span>}</td>
                    <td className="px-2 py-2 capitalize text-xs">{r.filing_frequency?.replace('_', ' ') || <span className="text-gray-400">—</span>}</td>
                    <td className="px-2 py-2 text-xs">{r.last_filed_at?.slice(0, 10) || <span className="text-gray-400">never</span>}</td>
                    <td className="px-2 py-2 text-center">
                      <button
                        onClick={() => toggleEnabled(r)}
                        className={`px-2 py-0.5 text-xs font-semibold rounded ${
                          r.collection_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {r.collection_enabled ? 'ON' : 'OFF'}
                      </button>
                    </td>
                    <td className="px-2 py-2 text-right">
                      <button onClick={() => startEdit(r)} className="text-blue-600 text-xs hover:underline">Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="text-sm text-gray-600">
        For nexus thresholds + sales-by-state risk view, see the
        <Link to="/reports/nexus" className="text-blue-600 underline mx-1">Economic Nexus Tracker</Link>.
      </div>

      {showPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm">
            <h3 className="text-lg font-semibold mb-3">Add a state to collect tax in</h3>
            {available.length === 0 ? (
              <p className="text-sm text-gray-500">All states are already configured. Use "Edit" on a row to change settings.</p>
            ) : (
              <>
                <p className="text-xs text-gray-500 mb-3">
                  Pick the state, then enter rate / permit / filing schedule on the next screen.
                </p>
                <select
                  value={pickerState}
                  onChange={(e) => setPickerState(e.target.value)}
                  className="w-full px-3 py-2 border rounded mb-3"
                >
                  {available.map(s => (
                    <option key={s.state_code} value={s.state_code}>{s.state_code} — {s.state}</option>
                  ))}
                </select>
              </>
            )}
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => setShowPicker(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded">Cancel</button>
              {available.length > 0 && (
                <button
                  onClick={() => {
                    const picked = available.find(s => s.state_code === pickerState);
                    if (!picked) return;
                    setShowPicker(false);
                    startEdit({
                      id: '',
                      state: picked.state,
                      state_code: picked.state_code,
                      threshold_amount: '0',
                      has_sales_tax: true,
                      wholesale_counts: false,
                      rate: null,
                      permit_number: null,
                      permit_registered_at: null,
                      filing_frequency: null,
                      last_filed_at: null,
                      collection_enabled: false,
                    });
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Continue
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-1">{editing.state} ({editing.state_code})</h3>
            <p className="text-xs text-gray-500 mb-4">Threshold tracking for this state lives on the Economic Nexus Tracker. Edit only the collection-side fields here.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs uppercase text-gray-500 mb-1">Rate (decimal)</label>
                <input
                  type="number" step="0.00001" min="0" max="0.2"
                  value={form.rate}
                  onChange={(e) => setForm(f => ({ ...f, rate: e.target.value }))}
                  className="w-full px-2 py-1 border rounded font-mono"
                  placeholder="e.g. 0.0825"
                />
                <p className="text-xs text-gray-400 mt-0.5">= {(parseFloat(form.rate) * 100 || 0).toFixed(3)}%</p>
              </div>
              <div>
                <label className="block text-xs uppercase text-gray-500 mb-1">Permit number</label>
                <input
                  type="text"
                  value={form.permit_number}
                  onChange={(e) => setForm(f => ({ ...f, permit_number: e.target.value }))}
                  className="w-full px-2 py-1 border rounded font-mono"
                  placeholder="e.g. 32099999999"
                />
              </div>
              <div>
                <label className="block text-xs uppercase text-gray-500 mb-1">Permit registered</label>
                <input
                  type="date"
                  value={form.permit_registered_at}
                  onChange={(e) => setForm(f => ({ ...f, permit_registered_at: e.target.value }))}
                  className="w-full px-2 py-1 border rounded"
                />
              </div>
              <div>
                <label className="block text-xs uppercase text-gray-500 mb-1">Filing frequency</label>
                <select
                  value={form.filing_frequency}
                  onChange={(e) => setForm(f => ({ ...f, filing_frequency: e.target.value }))}
                  className="w-full px-2 py-1 border rounded"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="semi_annual">Semi-annual</option>
                  <option value="annual">Annual</option>
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase text-gray-500 mb-1">Last filed</label>
                <input
                  type="date"
                  value={form.last_filed_at}
                  onChange={(e) => setForm(f => ({ ...f, last_filed_at: e.target.value }))}
                  className="w-full px-2 py-1 border rounded"
                />
              </div>
              <div className="col-span-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.collection_enabled}
                    onChange={(e) => setForm(f => ({ ...f, collection_enabled: e.target.checked }))}
                  />
                  <strong>Collect tax</strong> on new orders shipping to {editing.state_code}
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded">Cancel</button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ParishRatesPanel />
    </div>
  );
}
